import React from "react";
import { Box, Button, IconButton, Paper, Stack, Typography } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import type { ChildExtracurricular, ParentChild } from "../../../types/parentFamily";
import { formatCurrency } from "../../../utils/parentFamilyHelpers";
import { NoticeboardButton } from "../../shared";
import {
  MANAGEMENT_ACTION_ICON_BUTTON_SIZE,
  MANAGEMENT_ACTION_ICON_FONT_SIZE,
  MANAGEMENT_ACTION_ROW_SX,
} from "../../../styles/managementCardActions";
import { lightTheme } from "../../../styles/theme";
import { FAMILY_TEXT_LINK_SX } from "./familyStyles";
import type { FamilyDeleteTarget } from "./useFamilyModals";

interface ExtracurricularSectionProps {
  child: ParentChild;
  onOpenAddActivity: (childId: string) => void;
  onEditActivity: (childId: string, activity: ChildExtracurricular) => void;
  onDeleteActivity: (target: FamilyDeleteTarget) => void;
  onStartBillingFromActivity: (child: ParentChild, activity: ChildExtracurricular) => void;
}

export const ExtracurricularSection: React.FC<ExtracurricularSectionProps> = ({
  child,
  onOpenAddActivity,
  onEditActivity,
  onDeleteActivity,
  onStartBillingFromActivity,
}) => (
  <Stack spacing={1.5}>
    <Stack direction="row" justifyContent="space-between" alignItems="center">
      <Typography variant="subtitle2">Extracurricular</Typography>
      <NoticeboardButton
        variant="secondary"
        size="small"
        startIcon={<AddIcon />}
        onClick={() => onOpenAddActivity(child.id)}
      >
        Add activity
      </NoticeboardButton>
    </Stack>
    {child.extracurriculars.length === 0 ? (
      <Typography variant="body2" color="text.secondary">
        No activities for this family member.
      </Typography>
    ) : (
      child.extracurriculars.map((activity) => (
        <Paper key={activity.id} variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
            <Box>
              <Typography variant="body1">{activity.name}</Typography>
              {activity.schedule ? (
                <Typography variant="body2" color="text.secondary">
                  {activity.schedule}
                </Typography>
              ) : null}
              {activity.amount != null && activity.amount > 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  {formatCurrency(activity.amount, activity.currency)}
                </Typography>
              ) : null}
              {activity.amount != null && activity.amount > 0 ? (
                <Button
                  sx={{ ...FAMILY_TEXT_LINK_SX, mt: 0.75 }}
                  onClick={() => onStartBillingFromActivity(child, activity)}
                >
                  {(child.feeAccounts ?? []).some((a) => a.linkedActivityId === activity.id)
                    ? "View billing"
                    : "Start billing"}
                </Button>
              ) : null}
            </Box>
            <Box sx={MANAGEMENT_ACTION_ROW_SX}>
              <IconButton
                size={MANAGEMENT_ACTION_ICON_BUTTON_SIZE}
                onClick={() => onEditActivity(child.id, activity)}
                sx={{ color: lightTheme.colors.primary }}
                aria-label={`Edit ${activity.name}`}
              >
                <EditIcon fontSize={MANAGEMENT_ACTION_ICON_FONT_SIZE} />
              </IconButton>
              <IconButton
                size={MANAGEMENT_ACTION_ICON_BUTTON_SIZE}
                aria-label="Delete activity"
                onClick={() =>
                  onDeleteActivity({
                    type: "activity",
                    childId: child.id,
                    id: activity.id,
                    label: activity.name,
                  })
                }
                sx={{ color: lightTheme.colors.error }}
              >
                <DeleteIcon fontSize={MANAGEMENT_ACTION_ICON_FONT_SIZE} />
              </IconButton>
            </Box>
          </Stack>
        </Paper>
      ))
    )}
  </Stack>
);
